import { useState, useEffect } from "react";
import { mapperTypes, prospectArrayFields } from "../constants";

/**
 * Hook for managing mapper generation and updates
 */
export const useMapperManager = (
  mappingDrafts,
  ingestionMappings,
  prospectMappings,
  enrichConsumerMappings,
  arrayDefaults,
  formValues
) => {
  const [mappers, setMappers] = useState(
    mapperTypes.reduce((acc, type) => ({ ...acc, [type]: {} }), {})
  );

  useEffect(() => {
    const partnerName = formValues.partnerName || "default_partner";
    const updated = {};

    mapperTypes.forEach((type) => {
      if (type === "ingestion") {
        // For ingestion mapper, use ingestion mappings
        const newMapper = {};
        Object.entries(ingestionMappings).forEach(([sourceAttr, mapping]) => {
          if (mapping.include && mapping.include !== "" && mapping.targetField) {
            const entry = {};

            // Handle default mappings (no path since they don't exist in payload)
            if (mapping.isDefault) {
              entry.include = mapping.includeFunction || "() => true";
              if (mapping.modifierFunction) {
                entry.modifier = mapping.modifierFunction;
              }
            } else {
              // Regular mappings have path and include function
              entry.path = sourceAttr;
              entry.include = mapping.includeFunction || "(e) => e";
              if (mapping.modifierFunction) {
                entry.modifier = mapping.modifierFunction;
              }
            }

            newMapper[mapping.targetField] = entry;
          }
        });
        updated[type] = newMapper;
      } else if (type === "createProspect") {
        // For prospect mapper, use prospect mappings
        const newMapper = {};

        // Group mappings by prospect field
        const fieldGroups = {};
        prospectMappings.forEach((mapping) => {
          if (!fieldGroups[mapping.prospectField]) {
            fieldGroups[mapping.prospectField] = [];
          }
          fieldGroups[mapping.prospectField].push(mapping);
        });

        // Process each field group
        Object.entries(fieldGroups).forEach(([prospectField, mappings]) => {
          if (mappings.length > 1) {
            // Multiple mappings - create array modifier
            const arrayItems = mappings.map((mapping) => {
              const fieldName = mapping.ingestionField;
              if (prospectField === "consents") {
                return `{ createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}", consentType: "${fieldName}", granted: src.${fieldName} === true ? 1 : 0 }`;
              }
              if (prospectField === "commPreferences") {
                return `{ commPreferenceType: "${partnerName}", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}", channel: "${fieldName}", granted: 1 }`;
              }
              if (prospectField === "legalAcceptances") {
                return `{ legalAcceptanceType: "${fieldName}", granted: src.${fieldName} === "true" ? 1 : 0, createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }`;
              }
              if (prospectField === "enrichmentData") {
                return `{ enrichmentValue: src.${fieldName}, category: "default", enrichmentType: "custom", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }`;
              }
              return `{ value: src.${fieldName} }`;
            });

            // Add default values from arrayDefaults if they exist
            const defaults = arrayDefaults.createProspect[prospectField] || [];
            const defaultItems = defaults.map((defaultItem) => {
              if (prospectField === "consents") {
                return `{ createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}", consentType: "${defaultItem.value}", granted: ${defaultItem.granted ? 1 : 0} }`;
              }
              if (prospectField === "commPreferences") {
                return `{ commPreferenceType: "${defaultItem.commPreferenceType || partnerName}", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}", channel: "${defaultItem.value}", granted: ${defaultItem.granted ? 1 : 0} }`;
              }
              if (prospectField === "legalAcceptances") {
                return `{ legalAcceptanceType: "${defaultItem.value}", granted: ${defaultItem.granted ? 1 : 0}, createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }`;
              }
              if (prospectField === "enrichmentData") {
                return `{ enrichmentValue: "${defaultItem.value}", category: "${defaultItem.category || "default"}", enrichmentType: "${defaultItem.enrichmentType || "custom"}", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }`;
              }
              return `{ value: "${defaultItem.value}" }`;
            });

            const allItems = [...arrayItems, ...defaultItems];

            newMapper[prospectField] = {
              include: "(val, src) => true",
              modifier: `(val, src) => [${allItems.join(", ")}]`,
            };
          } else {
            // Single mapping or non-array field
            const mapping = mappings[0];
            const isSingleArrayField = prospectArrayFields.includes(mapping.prospectField);
            const defaults = arrayDefaults.createProspect[mapping.prospectField] || [];

            if (isSingleArrayField && defaults.length > 0) {
              // Single mapping but with default values - combine them
              const sourceItem = (() => {
                const fieldName = mapping.ingestionField;
                if (mapping.prospectField === "consents") {
                  return `{ createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}", consentType: "${fieldName}", granted: src.${fieldName} === true ? 1 : 0 }`;
                }
                if (mapping.prospectField === "commPreferences") {
                  return `{ commPreferenceType: "${partnerName}", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}", channel: "${fieldName}", granted: 1 }`;
                }
                if (mapping.prospectField === "legalAcceptances") {
                  return `{ legalAcceptanceType: "${fieldName}", granted: src.${fieldName} === "true" ? 1 : 0, createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }`;
                }
                if (mapping.prospectField === "enrichmentData") {
                  return `{ enrichmentValue: src.${fieldName}, category: "default", enrichmentType: "custom", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }`;
                }
                return `{ value: src.${fieldName} }`;
              })();

              const defaultItems = defaults.map((defaultItem) => {
                if (mapping.prospectField === "consents") {
                  return `{ createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}", consentType: "${defaultItem.value}", granted: ${defaultItem.granted ? 1 : 0} }`;
                }
                if (mapping.prospectField === "commPreferences") {
                  return `{ commPreferenceType: "${defaultItem.value}", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}", channel: "${defaultItem.channel || defaultItem.value}", granted: 1 }`;
                }
                if (mapping.prospectField === "legalAcceptances") {
                  return `{ legalAcceptanceType: "${defaultItem.value}", granted: ${defaultItem.granted ? 1 : 0}, createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }`;
                }
                if (mapping.prospectField === "enrichmentData") {
                  return `{ enrichmentValue: "${defaultItem.value}", category: "${defaultItem.category || "default"}", enrichmentType: "${defaultItem.enrichmentType || "custom"}", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }`;
                }
                return `{ value: "${defaultItem.value}" }`;
              });

              const allItems = [sourceItem, ...defaultItems];

              newMapper[mapping.prospectField] = {
                include: "(val, src) => true",
                modifier: `(val, src) => [${allItems.join(", ")}]`,
              };
            } else if (
              (!mapping.include || mapping.include.trim() === "(e) => e") &&
              !(mapping.modifier && mapping.modifier.trim())
            ) {
              // Regular single mapping - minimal string form for pass-through
              newMapper[mapping.prospectField] = mapping.path;
            } else {
              // Regular single mapping - full object form
              const obj = { path: mapping.path, include: mapping.include || "(e) => e" };
              if (mapping.modifier && mapping.modifier.trim()) {
                obj.modifier = mapping.modifier;
              }
              newMapper[mapping.prospectField] = obj;
            }
          }
        });

        // Handle array fields that only have default values (no mapped fields)
        prospectArrayFields.forEach((arrayField) => {
          const defaults = arrayDefaults.createProspect[arrayField] || [];
          const hasMapping = fieldGroups[arrayField];

          if (!hasMapping && defaults.length > 0) {
            // Array field has only default values
            const defaultItems = defaults.map((defaultItem) => {
              if (arrayField === "consents") {
                return `{ createdAt: new Date().toISOString(), createdBy: "${partnerName}", updatedAt: new Date().toISOString(), updatedBy: "${partnerName}", consentType: "${defaultItem.value}", granted: ${defaultItem.granted ? 1 : 0} }`;
              }
              if (arrayField === "commPreferences") {
                return `{ commPreferenceType: "${defaultItem.commPreferenceType || partnerName}", createdAt: new Date().toISOString(), createdBy: "${partnerName}", updatedAt: new Date().toISOString(), updatedBy: "${partnerName}", channel: "${defaultItem.value}", granted: 1 }`;
              }
              if (arrayField === "legalAcceptances") {
                return `{ legalAcceptanceType: "${defaultItem.value}", granted: ${defaultItem.granted ? 1 : 0}, createdAt: new Date().toISOString(), createdBy: "${partnerName}", updatedAt: new Date().toISOString(), updatedBy: "${partnerName}" }`;
              }
              if (arrayField === "enrichmentData") {
                return `{ enrichmentValue: "${defaultItem.value}", category: "${defaultItem.category || "default"}", enrichmentType: "${defaultItem.enrichmentType || "custom"}", createdAt: new Date().toISOString(), createdBy: "${partnerName}", updatedAt: new Date().toISOString(), updatedBy: "${partnerName}" }`;
              }
              return `{ value: "${defaultItem.value}" }`;
            });

            newMapper[arrayField] = {
              include: "(val, src) => true",
              modifier: `(val, src) => [${defaultItems.join(", ")}]`,
            };
          }
        });

        updated[type] = newMapper;
      } else if (type === "enrichConsumer") {
        // For enrich consumer mapper, similar logic but with different data source
        const newMapper = {};

        // Group mappings by prospect field
        const fieldGroups = {};
        enrichConsumerMappings.forEach((mapping) => {
          if (!fieldGroups[mapping.prospectField]) {
            fieldGroups[mapping.prospectField] = [];
          }
          fieldGroups[mapping.prospectField].push(mapping);
        });

        // Process each field group
        Object.entries(fieldGroups).forEach(([prospectField, mappings]) => {
          if (mappings.length > 1) {
            // Multiple mappings - create array modifier
            const arrayItems = mappings.map((mapping) => {
              const fieldName = mapping.ingestionField;
              if (prospectField === "consents") {
                return `{ createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}", consentType: "${fieldName}", granted: src.${fieldName} === true ? 1 : 0 }`;
              }
              if (prospectField === "commPreferences") {
                return `{ commPreferenceType: "${fieldName}", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}", channel: "${fieldName}", granted: 1 }`;
              }
              if (prospectField === "legalAcceptances") {
                return `{ legalAcceptanceType: "${fieldName}", granted: src.${fieldName} === "true" ? 1 : 0, createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }`;
              }
              if (prospectField === "enrichmentData") {
                return `{ enrichmentValue: src.${fieldName}, category: "default", enrichmentType: "custom", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }`;
              }
              return `{ value: src.${fieldName} }`;
            });

            // Add default values from arrayDefaults if they exist
            const defaults = arrayDefaults.enrichConsumer[prospectField] || [];
            const defaultItems = defaults.map((defaultItem) => {
              if (prospectField === "consents") {
                return `{ createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}", consentType: "${defaultItem.value}", granted: ${defaultItem.granted ? 1 : 0} }`;
              }
              if (prospectField === "commPreferences") {
                return `{ commPreferenceType: "${defaultItem.value}", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}", channel: "${defaultItem.channel || defaultItem.value}", granted: 1 }`;
              }
              if (prospectField === "legalAcceptances") {
                return `{ legalAcceptanceType: "${defaultItem.value}", granted: ${defaultItem.granted ? 1 : 0}, createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }`;
              }
              if (prospectField === "enrichmentData") {
                return `{ enrichmentValue: "${defaultItem.value}", category: "${defaultItem.category || "default"}", enrichmentType: "${defaultItem.enrichmentType || "custom"}", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }`;
              }
              return `{ value: "${defaultItem.value}" }`;
            });

            const allItems = [...arrayItems, ...defaultItems];

            newMapper[prospectField] = {
              include: "(val, src) => true",
              modifier: `(val, src) => [${allItems.join(", ")}]`,
            };
          } else {
            // Single mapping or non-array field
            const mapping = mappings[0];
            const isSingleArrayField = prospectArrayFields.includes(mapping.prospectField);
            const defaults = arrayDefaults.enrichConsumer[mapping.prospectField] || [];

            if (isSingleArrayField && defaults.length > 0) {
              // Single mapping but with default values - combine them
              const sourceItem = (() => {
                const fieldName = mapping.ingestionField;
                if (mapping.prospectField === "consents") {
                  return `{ createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}", consentType: "${fieldName}", granted: src.${fieldName} === "true" ? 1 : 0 }`;
                }
                if (mapping.prospectField === "commPreferences") {
                  return `{ commPreferenceType: "${fieldName}", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}", channel: "${fieldName}", granted: 1 }`;
                }
                if (mapping.prospectField === "legalAcceptances") {
                  return `{ legalAcceptanceType: "${fieldName}", granted: src.${fieldName} === "true" ? 1 : 0, createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }`;
                }
                if (mapping.prospectField === "enrichmentData") {
                  return `{ enrichmentValue: src.${fieldName}, category: "default", enrichmentType: "custom", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }`;
                }
                return `{ value: src.${fieldName} }`;
              })();

              const defaultItems = defaults.map((defaultItem) => {
                if (mapping.prospectField === "consents") {
                  return `{ createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}", consentType: "${defaultItem.value}", granted: ${defaultItem.granted ? 1 : 0} }`;
                }
                if (mapping.prospectField === "commPreferences") {
                  return `{ commPreferenceType: "${defaultItem.value}", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}", channel: "${defaultItem.channel || defaultItem.value}", granted: 1 }`;
                }
                if (mapping.prospectField === "legalAcceptances") {
                  return `{ legalAcceptanceType: "${defaultItem.value}", granted: ${defaultItem.granted ? 1 : 0}, createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }`;
                }
                if (mapping.prospectField === "enrichmentData") {
                  return `{ enrichmentValue: "${defaultItem.value}", category: "${defaultItem.category || "default"}", enrichmentType: "${defaultItem.enrichmentType || "custom"}", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}" }`;
                }
                return `{ value: "${defaultItem.value}" }`;
              });

              const allItems = [sourceItem, ...defaultItems];

              newMapper[mapping.prospectField] = {
                include: "(val, src) => true",
                modifier: `(val, src) => [${allItems.join(", ")}]`,
              };
            } else if (
              (!mapping.include || mapping.include.trim() === "(e) => e") &&
              !(mapping.modifier && mapping.modifier.trim())
            ) {
              // Regular single mapping - minimal string form
              newMapper[mapping.prospectField] = mapping.path;
            } else {
              // Regular single mapping - full object form
              const obj = { path: mapping.path, include: mapping.include || "(e) => e" };
              if (mapping.modifier && mapping.modifier.trim()) {
                obj.modifier = mapping.modifier;
              }
              newMapper[mapping.prospectField] = obj;
            }
          }
        });

        // Handle array fields that only have default values (no mapped fields)
        prospectArrayFields.forEach((arrayField) => {
          const defaults = arrayDefaults.enrichConsumer[arrayField] || [];
          const hasMapping = fieldGroups[arrayField];

          if (!hasMapping && defaults.length > 0) {
            // Array field has only default values
            const defaultItems = defaults.map((defaultItem) => {
              if (arrayField === "consents") {
                return `{ createdAt: new Date().toISOString(), createdBy: "${partnerName}", updatedAt: new Date().toISOString(), updatedBy: "${partnerName}", consentType: "${defaultItem.value}", granted: ${defaultItem.granted ? 1 : 0} }`;
              }
              if (arrayField === "commPreferences") {
                return `{ commPreferenceType: "${defaultItem.value}", createdAt: new Date().toISOString(), createdBy: "${partnerName}", updatedAt: new Date().toISOString(), updatedBy: "${partnerName}", channel: "${defaultItem.channel || defaultItem.value}", granted: 1 }`;
              }
              if (arrayField === "legalAcceptances") {
                return `{ legalAcceptanceType: "${defaultItem.value}", granted: ${defaultItem.granted ? 1 : 0}, createdAt: new Date().toISOString(), createdBy: "${partnerName}", updatedAt: new Date().toISOString(), updatedBy: "${partnerName}" }`;
              }
              if (arrayField === "enrichmentData") {
                return `{ enrichmentValue: "${defaultItem.value}", category: "${defaultItem.category || "default"}", enrichmentType: "${defaultItem.enrichmentType || "custom"}", createdAt: new Date().toISOString(), createdBy: "${partnerName}", updatedAt: new Date().toISOString(), updatedBy: "${partnerName}" }`;
              }
              return `{ value: "${defaultItem.value}" }`;
            });

            newMapper[arrayField] = {
              include: "(val, src) => true",
              modifier: `(val, src) => [${defaultItems.join(", ")}]`,
            };
          }
        });

        updated[type] = newMapper;
      }
    });

    setMappers(updated);
  }, [
    mappingDrafts,
    ingestionMappings,
    prospectMappings,
    enrichConsumerMappings,
    arrayDefaults,
    formValues.partnerName,
  ]);

  return {
    mappers,
    setMappers,
  };
};
